import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, Upload, FileText, Download, FolderOpen, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);
const isImage = (ct) => (ct || "").startsWith("image/");
const isPdf = (ct) => ct === "application/pdf";
const canPreview = (ct) => isImage(ct) || isPdf(ct);

// Petit signal sonore (Web Audio) — pas d'asset requis
function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.03);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    o.start(); o.stop(ctx.currentTime + 0.36);
    o.onended = () => ctx.close();
  } catch { /* silencieux */ }
}

// mode: "host" -> aid ; "patient" -> token
export function ConsultationDocs({ mode = "host", aid, token }) {
  const base = mode === "host" ? `/admin/visio/appointments/${aid}/documents` : `/visio/session/${token}/documents`;
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dlId, setDlId] = useState(null);
  const [preview, setPreview] = useState(null); // {name, url, ct}
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileRef = useRef(null);
  const meLabel = mode === "host" ? "medecin" : "patient";
  const knownRef = useRef(null); // Set des ids déjà vus (null = pas encore chargé)

  const load = useCallback(() => {
    api.get(base).then((r) => {
      const list = r.data || [];
      // Notification live des documents reçus de l'autre partie
      if (knownRef.current === null) {
        knownRef.current = new Set(list.map((d) => d.id));
      } else {
        const fresh = list.filter((d) => !knownRef.current.has(d.id) && d.uploader !== meLabel);
        if (fresh.length) {
          beep();
          const who = meLabel === "medecin" ? "le patient" : "le médecin";
          toast.info(fresh.length === 1
            ? `Nouveau document partagé par ${who} : ${fresh[0].name}`
            : `${fresh.length} nouveaux documents partagés par ${who}`);
        }
        list.forEach((d) => knownRef.current.add(d.id));
      }
      setDocs(list);
    }).catch(() => {});
  }, [base, meLabel]);

  useEffect(() => {
    if ((mode === "host" && !aid) || (mode === "patient" && !token)) return;
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load, mode, aid, token]);

  const onPick = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 15 Mo)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(base, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document partagé");
      load();
    } catch (err) { toast.error(apiError(err, "Envoi impossible")); } finally { setUploading(false); }
  };

  const fetchBlob = async (d) => api.get(`${base}/${d.id}`, { responseType: "blob" });

  const download = async (d) => {
    setDlId(d.id);
    try {
      const res = await fetchBlob(d);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = d.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch { toast.error("Téléchargement impossible"); } finally { setDlId(null); }
  };

  const openPreview = async (d) => {
    setPreviewLoading(true);
    try {
      const res = await fetchBlob(d);
      const url = URL.createObjectURL(res.data);
      setPreview({ name: d.name, url, ct: d.content_type });
    } catch { toast.error("Aperçu indisponible"); } finally { setPreviewLoading(false); }
  };
  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="consultation-docs">
      <div className="mb-3 flex items-center gap-2">
        <FolderOpen size={16} className="text-ciel" />
        <h3 className="font-display text-sm font-semibold text-navy">Documents partagés</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{docs.length}</span>
        <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="consultation-docs-input"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv" />
        <Button size="sm" onClick={onPick} disabled={uploading} data-testid="consultation-docs-upload"
          className="ml-auto h-8 rounded-full bg-ciel px-3 text-xs text-white hover:bg-sky-700">
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <><Upload size={13} className="mr-1" /> Partager un fichier</>}
        </Button>
      </div>
      {docs.length === 0 ? (
        <p className="py-4 text-center text-xs text-slate-400" data-testid="consultation-docs-empty">
          Aucun document. Partagez une ordonnance, un examen, une pièce d'identité…
        </p>
      ) : (
        <ul className="space-y-2" data-testid="consultation-docs-list">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 p-2" data-testid={`consultation-doc-${d.id}`}>
              <FileText size={16} className="shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-navy">{d.name}</p>
                <p className="text-[10px] text-slate-400">
                  <span className={d.uploader === meLabel ? "text-emerald-600" : "text-ciel"}>
                    {d.uploader === "medecin" ? "Médecin" : "Patient"}{d.uploader === meLabel ? " (vous)" : ""}
                  </span>
                  {d.size ? ` · ${fmtSize(d.size)}` : ""}
                </p>
              </div>
              {canPreview(d.content_type) && (
                <Button size="sm" variant="ghost" onClick={() => openPreview(d)} disabled={previewLoading}
                  data-testid={`consultation-doc-preview-${d.id}`} className="h-7 shrink-0 px-2 text-navy hover:bg-navy/5" title="Aperçu">
                  <Eye size={14} />
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => download(d)} disabled={dlId === d.id}
                data-testid={`consultation-doc-download-${d.id}`} className="h-7 shrink-0 px-2 text-ciel hover:bg-ciel/10" title="Télécharger">
                {dlId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={14} />}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-4xl" data-testid="consultation-doc-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate pr-6 text-sm">{preview?.name}</DialogTitle>
          </DialogHeader>
          {preview && (isImage(preview.ct) ? (
            <img src={preview.url} alt={preview.name} className="max-h-[70vh] w-full rounded-lg object-contain" data-testid="consultation-doc-preview-image" />
          ) : (
            <iframe src={preview.url} title={preview.name} className="h-[70vh] w-full rounded-lg border border-slate-200" data-testid="consultation-doc-preview-pdf" />
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
